import { Button, styled, Typography } from '@mui/material';
import { useAtomValue } from 'jotai';
import { useCallback, useState } from 'react';
import { Card } from '../../../components/Card/Card';
import { BasicDialog } from '../../../components/Dialog/BasicDialog';
import { StickyFormButtons } from '../../../components/Form/StickyFormButtons';
import { BasicDragHandle, SortableItem } from '../../../components/SortableList/SortableItem';
import { MultiSortableList } from '../../../components/SortableMultiList/MultiSortableList';
import { IFundDataModel } from '../../../data-models/fund.data-model';
import { FundDataModel } from '../../../schemas/Fund.schema';
import { selectableFundsAtom } from '../../../services/state/AppConfigStateJ';
import { fundComparator } from '../../PortfolioOverview/state/FundFilterStateJ';
import { useConfigActions } from '../Common/UseConfigActions';

const CardWrapper = styled('div')`
  display: grid;
  grid-template-columns: 1fr min-content;
  align-items: center;
  width: 100%;
`;

const ContentWrapper = styled('div')`
  padding: 2rem;
`;

export interface IFundSortProps {
  closeModal: () => void;
}

export function getFundsByTypeSorted(funds: FundDataModel[]) {
  const sortedFunds = [...funds].sort(fundComparator);
  return sortedFunds.reduce(
    (res, fund) => {
      if (!res[fund.fundType]) {
        res[fund.fundType] = [];
      }
      res[fund.fundType]!.push(fund);
      return res;
    },
    {} as Record<string, IFundDataModel[]>
  );
}

export function FundSort(props: IFundSortProps) {
  const { bulkUpdateItems } = useConfigActions<FundDataModel>('/fund');
  const universalFundsFields = [...(useAtomValue(selectableFundsAtom) ?? [])].sort(fundComparator);
  const fundsByType = getFundsByTypeSorted(universalFundsFields);
  const [updatedItems, setUpdatedItems] = useState(fundsByType);

  function onOrderChange(items: Record<string, FundDataModel[]>) {
    setUpdatedItems(items);
  }

  function onSaveUpdatedOrder() {
    const payload: FundDataModel[] = [];
    let index = 0;

    Object.keys(updatedItems).forEach((fundType) => {
      const fundsForType = updatedItems[fundType];
      fundsForType.forEach((found) => {
        payload.push(found);
        found.sortOrder = index;
        index++;
      });
    });

    bulkUpdateItems(payload);
  }

  return (
    <ContentWrapper>
      <MultiSortableList
        items={updatedItems}
        onChange={onOrderChange}
        disableCrossGroupMove
        renderItem={(item) => {
          return (
            <SortableItem id={item.id}>
              <Card>
                <CardWrapper>
                  <Typography>{item.name}</Typography>
                  <BasicDragHandle />
                </CardWrapper>
              </Card>
            </SortableItem>
          );
        }}
      />
      <StickyFormButtons onCancel={props.closeModal} onSubmit={onSaveUpdatedOrder} submitLabel={'Save'} />
    </ContentWrapper>
  );
}

export function SortFunds() {
  const [open, setOpen] = useState(false);
  const openModal = useCallback(() => {
    setOpen(true);
  }, []);
  const closeModal = useCallback(() => {
    setOpen(false);
  }, []);

  return (
    <>
      <Button color={'secondary'} onClick={openModal} variant={'contained'}>
        Sort Funds
      </Button>
      {open && (
        <BasicDialog open={open} onClose={closeModal} title={'Sort Funds'}>
          <FundSort closeModal={closeModal} />
        </BasicDialog>
      )}
    </>
  );
}
